function getSplitParts(element) {
  const splitMode = element.dataset.split || "words";
  if (splitMode === "chars") return element.querySelectorAll(".char");
  if (splitMode === "lines") return element.querySelectorAll(".line");
  return element.querySelectorAll(".word");
}

/** Seconds; from `data-scroll-animation-delay` (or `data-animation-delay`). */
function getAnimationDelay(element) {
  const raw = element.dataset.scrollAnimationDelay ?? element.dataset.animationDelay;
  if (raw == null || raw === "") return 0;
  const n = parseFloat(String(raw).trim());
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

/** Seconds; from `data-scroll-animation-duration` (or `data-animation-duration`). */
function getAnimationDuration(element, fallback) {
  const raw = element.dataset.scrollAnimationDuration ?? element.dataset.animationDuration;
  if (raw == null || raw === "") return fallback;
  const n = parseFloat(String(raw).trim());
  return Number.isFinite(n) && n >= 0 ? n : fallback;
}

/** Total time (s) spread across all staggered parts; from `data-scroll-animation-stagger`. */
function getStaggerAmount(element, fallback) {
  const raw = element.dataset.scrollAnimationStagger;
  if (raw == null || raw === "") return fallback;
  const n = parseFloat(String(raw).trim());
  return Number.isFinite(n) && n >= 0 ? n : fallback;
}

/** Starting yPercent for `textSlideUp`; from `data-scroll-animation-y`. */
function getAnimationYPercent(element, fallback) {
  const raw = element.dataset.scrollAnimationY;
  if (raw == null || raw === "") return fallback;
  const n = parseFloat(String(raw).trim());
  return Number.isFinite(n) ? n : fallback;
}

function getAnimationSelector(element, fallback) {
  const raw = element.dataset.scrollAnimationSelector;
  if (!raw || !raw.trim()) return fallback;
  return raw.trim();
}

/** Step in seconds between child items; from `data-scroll-animation-stagger`. */
function getStaggerStep(element, fallback) {
  const raw = element.dataset.scrollAnimationStagger;
  if (raw == null || raw === "") return fallback;
  const n = parseFloat(String(raw).trim());
  return Number.isFinite(n) && n >= 0 ? n : fallback;
}

function getTargetsFromSelector(element) {
  const selector = getAnimationSelector(element, "");
  return selector ? Array.from(element.querySelectorAll(selector)) : [element];
}

function modifyDomForImageReveal(element) {
  const targets = getTargetsFromSelector(element);

  targets.forEach((target) => {
    if (!target.querySelector("img")) return;

    let overlay = target.querySelector(".image-reveal");
    if (!overlay) {
      overlay = document.createElement("span");
      overlay.className = "image-reveal";
      overlay.setAttribute("aria-hidden", "true");
      target.prepend(overlay);
    }
  });
}

function textSlideUp(gsap, element) {
  const parts = getSplitParts(element);
  if (!parts.length) return false;

  const delay = getAnimationDelay(element);
  const count = parts.length;
  const staggerAmount = getStaggerAmount(element, Math.min(0.45, 0.06 * count));
  const yPercent = getAnimationYPercent(element, 150);

  // Start at opacity 0 so blur isn't visible on the first frame.
  gsap.fromTo(
    parts,
    {
      yPercent,
      rotate: 9,
      opacity: 0,
      filter: "blur(20px)",
      webkitFilter: "blur(20px)",
      transformOrigin: "50% 100%",
    },
    {
      yPercent: 0,
      rotate: 0,
      opacity: 1,
      filter: "blur(0px)",
      webkitFilter: "blur(0px)",
      duration: getAnimationDuration(element, 1.75),
      delay,
      stagger: { amount: staggerAmount, from: "start" },
      ease: element.closest(".hero-clip, .hero-media, .hero-img, .hero-caption-img") ? "expo.out" : "power4.out",
      clearProps: "transform,filter,webkitFilter",
      onComplete: () => {
        gsap.set(parts, { opacity: 1 });
      },
    },
  );
  return true;
}

/** True when `textReveal` should stagger `.char` nodes (Harbor-style). */
function shouldRevealWithChars(element) {
  if (element.dataset.scrollAnimationReveal === "block") return false;
  if (element.dataset.scrollAnimationReveal === "chars") return true;
  if (element.dataset.split === "chars") return true;
  return false;
}

/**
 * Letter-by-letter reveal (reusable). Requires Splitting `data-split="chars"` on `element`.
 * @param {object} overrides - optional `{ delay, staggerCap }`
 */
export function textRevealChars(gsap, element, overrides = {}) {
  const parts = element.querySelectorAll(".char");
  if (!parts.length) return false;

  const delay = overrides.delay ?? getAnimationDelay(element);
  const count = parts.length;
  const staggerAmount = overrides.staggerAmount ?? getStaggerAmount(element, Math.min(0.55, 0.018 * count));

  // No rotateX — 3D on inline .char often disappears behind backface in WebKit when paired with parent perspective.
  gsap.fromTo(
    parts,
    {
      yPercent: 85,
      opacity: 0,
      transformOrigin: "50% 100%",
    },
    {
      yPercent: 0,
      opacity: 1,
      duration: 0.9,
      delay,
      stagger: { amount: staggerAmount, from: "start" },
      ease: "power3.out",
      clearProps: "transform,opacity",
      onComplete: () => {
        gsap.set(parts, { opacity: 1, clearProps: "transform" });
      },
    },
  );
  return true;
}

function textRevealBlock(gsap, element) {
  const delay = getAnimationDelay(element);

  gsap.fromTo(
    element,
    { y: -100, rotate: -7, opacity: 0, transformOrigin: "50% 0%" },
    {
      y: 0,
      rotate: 0,
      opacity: 1,
      duration: 1.05,
      delay,
      ease: "power4.out",
      clearProps: "transform,opacity",
    },
  );
  return true;
}

function textReveal(gsap, element) {
  if (shouldRevealWithChars(element)) {
    const ok = textRevealChars(gsap, element);
    if (ok) return true;
  }
  return textRevealBlock(gsap, element);
}

function imageReveal(gsap, element) {
  const delay = getAnimationDelay(element);
  const step = getStaggerStep(element, 0.2);
  const items = [];
  const targets = getTargetsFromSelector(element);
  targets.forEach((target) => {
    const image = target.querySelector("img");
    const overlay = target.querySelector(".image-reveal");
    if (!image || !overlay) return;
    items.push({ target, image, overlay });
  });
  if (!items.length) return false;

  const tl = gsap.timeline({ delay, defaults: { ease: "expo.inOut" } });
  items.forEach(({ image, overlay }, index) => {
    const at = index * step;

    tl.fromTo(image, { scale: 2.5, opacity: 0.5 }, { scale: 1, opacity: 1, duration: 1.05 }, at);
    tl.fromTo(overlay, { scaleY: 1, transformOrigin: "top center" }, { scaleY: 0, duration: 0.88 }, at);
  });

  return true;
}

const ANIMATIONS = {
  textSlideUp,
  textReveal,
  imageReveal,
};

function getCallAnimation(detail, target) {
  const datasetAnimation = target?.dataset?.scrollAnimation || target?.dataset?.animation;
  if (datasetAnimation) return datasetAnimation;

  const values = detail?.values;
  if (Array.isArray(values) && values.length) {
    const first = values[0];
    if (first && first !== "animate") return first;
  }
  if (typeof values === "string" && values && values !== "animate") return values;
  return null;
}

export function initAnimations(loco) {
  const gsap = window.gsap;
  if (!gsap) return;

  const elements = Array.from(document.querySelectorAll("[data-scroll-animation], [data-animation]"));
  elements.forEach((element) => {
    const animation = element.dataset.scrollAnimation || element.dataset.animation;
    if (!animation || !ANIMATIONS[animation]) return;

    if (animation === "imageReveal") {
      modifyDomForImageReveal(element);
    }

    if (animation === "textSlideUp") {
      const parts = getSplitParts(element);
      if (parts.length) gsap.set(parts, { opacity: 0 });
    }

    element.setAttribute("data-scroll", "");
    if (!element.hasAttribute("data-scroll-call")) {
      element.setAttribute("data-scroll-call", "animate");
    }
  });

  if (loco && typeof loco.resize === "function") {
    loco.resize();
  }

  const runIfInview = (element) => {
    if (!element || element.dataset.animated === "true") return false;
    const isMarkedInview = element.classList.contains("is-inview");
    const rect = element.getBoundingClientRect?.();
    const isInViewport = rect && rect.bottom > window.innerHeight * 0.12 && rect.top < window.innerHeight * 0.88;
    if (!isMarkedInview && !isInViewport) return false;
    const animation = element.dataset.scrollAnimation || element.dataset.animation;
    const run = ANIMATIONS[animation];
    if (!run) return false;
    const didRun = run(gsap, element);
    if (didRun) element.dataset.animated = "true";
    return didRun;
  };

  const checkAll = () => {
    elements.forEach(runIfInview);
  };

  // Initial run (e.g. elements already in view on load/hash navigation).
  checkAll();

  const lenis = loco?.lenisInstance;
  if (typeof loco?.subscribeLenisScroll === "function") {
    loco.subscribeLenisScroll(checkAll);
  } else if (lenis && typeof lenis.on === "function") {
    lenis.on("scroll", checkAll);
  } else {
    window.addEventListener("scroll", checkAll, { passive: true });
  }

  window.addEventListener("animate", (event) => {
    const detail = event?.detail || {};
    if (detail.way !== "enter") return;

    const target = detail.target;
    if (!target || target.dataset.animated === "true") return;

    const animation = getCallAnimation(detail, target);
    const run = ANIMATIONS[animation];
    if (!run) return;

    const didRun = run(gsap, target);
    if (didRun) target.dataset.animated = "true";
  });

  requestAnimationFrame(() => {
    checkAll();
    requestAnimationFrame(checkAll);
  });
}
